// Review API service for Transcontinental Network
// Handles all review-related API calls

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

class ReviewApiService {
    constructor() {
        this.token = localStorage.getItem('access_token');
    }

    getHeaders(includeAuth = true) {
        const headers = {
            'Content-Type': 'application/json'
        };

        if (includeAuth) {
            const token = localStorage.getItem('access_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }

        return headers;
    }

    async request(endpoint, options = {}) {
        const url = `${API_BASE_URL}${endpoint}`;

        const config = {
            headers: this.getHeaders(options.auth !== false),
            ...options,
        };

        try {
            const response = await fetch(url, config);
            const contentType = response.headers.get('content-type');

            let data;
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                if (response.ok) {
                    return { success: true, data: text };
                }
                throw new Error(`Server returned non-JSON response: ${response.status}`);
            }

            if (!response.ok) {
                const error = new Error(data.message || `Request failed with status ${response.status}`);
                error.status = response.status;
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Review API Request failed:', { endpoint, error: error.message });
            throw error;
        }
    }

    // ===== PUBLIC ENDPOINTS =====

    // Get all approved reviews with optional filters
    async getApprovedReviews(params = {}) {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.rating) queryParams.append('rating', params.rating);
        if (params.min_rating) queryParams.append('min_rating', params.min_rating);
        if (params.verified_only) queryParams.append('verified_only', params.verified_only);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);

        const queryString = queryParams.toString();
        const endpoint = `/reviews${queryString ? `?${queryString}` : ''}`;

        return await this.request(endpoint, { method: 'GET', auth: false });
    }

    // Get review statistics
    async getReviewStats() {
        return await this.request('/reviews/stats', { method: 'GET', auth: false });
    }

    // Create a new review (guest or authenticated)
    async createReview(reviewData) {
        const token = localStorage.getItem('access_token');

        return await this.request('/reviews', {
            method: 'POST',
            auth: !!token,
            body: JSON.stringify(reviewData),
        });
    }

    // ===== AUTHENTICATED USER ENDPOINTS =====

    // Get user info for review form (name, email, has_reviewed, is_verified)
    async getUserInfo() {
        return await this.request('/reviews/user-info', { method: 'GET', auth: true });
    }

    // Get all reviews by the current user
    async getMyReviews() {
        return await this.request('/reviews/my-reviews', { method: 'GET', auth: true });
    }

    // Update user's own review
    async updateReview(reviewId, reviewData) {
        return await this.request(`/reviews/${reviewId}`, {
            method: 'PUT',
            auth: true,
            body: JSON.stringify(reviewData),
        });
    }

    // ===== ADMIN ENDPOINTS =====

    // Get all reviews including pending/rejected (admin only)
    async getAdminReviews(params = {}) {
        const queryParams = new URLSearchParams();

        if (params.status) queryParams.append('status', params.status);
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);

        const queryString = queryParams.toString();
        const endpoint = `/admin/reviews${queryString ? `?${queryString}` : ''}`;

        return await this.request(endpoint, { method: 'GET', auth: true });
    }

    // Update review status (admin only)
    async updateReviewStatus(reviewId, status) {
        return await this.request(`/reviews/${reviewId}/status`, {
            method: 'PUT',
            auth: true,
            body: JSON.stringify({ status }),
        });
    }

    // Delete a review (admin only)
    async deleteReview(reviewId) {
        return await this.request(`/reviews/${reviewId}`, {
            method: 'DELETE',
            auth: true,
        });
    }
}

const reviewApi = new ReviewApiService();
export default reviewApi;
